/*
 * WELLF — JavaScript error beacon  (ADDITIVE, isolated, site-wide)
 * ---------------------------------------------------------------------------
 * Reports uncaught JS errors, unhandled promise rejections and failed asset loads:
 *   GA4: gtag('event', 'wellf_js_error', {...})
 *
 * WHY THIS EXISTS
 * The storefront is heavily bespoke: the buy card, gallery, accordions, modals, cart
 * and spin wheel are all our own JS. If any of it throws on a browser we do not test
 * on (an older iOS Safari, an in-app webview from Instagram), the control silently
 * stops working and we have no way of knowing. Nothing in GA4 or Shopify surfaces a
 * thrown exception. We found that out the hard way in August, when the whole
 * behavioural layer died for three days without a single visible symptom.
 *
 * In-app webviews matter here specifically: paid social traffic arrives inside the
 * Instagram browser, which is not a browser we ever test in.
 *
 * GA4 ONLY — deliberately not sent to Meta, same reasoning as wellf-vitals.js.
 *
 * SAFETY / ISOLATION
 *  - Site-wide, but ONLY when settings.enable_wellf_errors is on (pure kill-switch).
 *  - Listeners only. Never rethrows, never swallows: handlers do not call
 *    preventDefault, so the browser still logs to the console exactly as before.
 *  - Hard caps: at most MAX_PER_PAGE reports per pageview, deduplicated by
 *    message+file+line, so one error in a loop cannot flood GA4 or the visitor.
 *  - Browser-extension noise is dropped. The founder's own console shows extension
 *    errors on every page load; those are not our bugs and would drown the signal.
 *
 * CONSENT
 * Same gate as the rest of the layer: marketing AND analytics. A fault that happens
 * before consent is held in memory (capped, never stored) and delivered if consent is
 * later granted. This is NOT the "never replay pre-consent behaviour" rule being bent:
 * that rule protects a visitor's ACTIONS. A stack trace is a fault in our own code,
 * not something the visitor did.
 *
 * KNOWN BLIND SPOT
 * An error that breaks the consent banner itself, or that happens in a session where
 * consent is refused, is invisible to us. There is no transport for it, because every
 * transport we have is consent-gated. If a whole-page breakage is ever suspected, this
 * beacon cannot rule it out.
 */
(function () {
  'use strict';

  var CS = document.currentScript;
  var DEBUG = !!(CS && CS.getAttribute('data-debug') === 'true');
  var PAGE = (CS && CS.getAttribute('data-page')) || 'other';

  var MAX_PER_PAGE = 5;
  var sentCount = 0;
  // One id per pageview, same reason as wellf-vitals.js: GA4's transport retries in the
  // Instagram in-app browser and delivers the same event twice. COUNT(DISTINCT vid) is
  // the honest count of pageviews that threw, rather than of deliveries.
  var vid = '';
  try { vid = Math.random().toString(36).slice(2, 10) + Date.now().toString(36); } catch (e) { vid = ''; }
  var seen = Object.create(null);
  var pending = [];

  // ── ask Shopify for the real Customer Privacy API (see wellf-events.js) ──
  try {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }]);
    }
  } catch (e) {}

  function consentOK() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || !cp.marketingAllowed || !cp.analyticsProcessingAllowed) return false;
      return cp.marketingAllowed() === true && cp.analyticsProcessingAllowed() === true;
    } catch (e) { return false; }
  }

  function clip(s, n) {
    try { return String(s == null ? '' : s).slice(0, n); } catch (e) { return ''; }
  }

  // Keep only the filename, not the full CDN URL with its cache-busting query string.
  function shortFile(url) {
    try {
      if (!url) return '';
      return clip(String(url).split('?')[0].split('/').pop(), 60);
    } catch (e) { return ''; }
  }

  // Extension errors are not our bugs and would swamp everything else.
  function isNoise(file) {
    try {
      return /^(chrome|moz|safari|ms-browser|webkit)-extension:/.test(String(file || ''));
    } catch (e) { return false; }
  }

  function deliver(p) {
    try {
      if (typeof window.gtag !== 'function') return false;
      window.gtag('event', 'wellf_js_error', p);
      return true;
    } catch (e) { return false; }
  }

  function flush() {
    if (!pending.length || !consentOK()) return;
    var queued = pending.slice();
    pending.length = 0;
    for (var i = 0; i < queued.length; i++) {
      if (!deliver(queued[i])) { pending.push(queued[i]); break; }
    }
  }

  function report(type, msg, file, line, col) {
    if (sentCount >= MAX_PER_PAGE) return;
    if (isNoise(file)) return;

    var key = type + '|' + clip(msg, 80) + '|' + shortFile(file) + '|' + (line || 0);
    if (seen[key]) return;
    seen[key] = true;
    sentCount++;

    var p = {
      page: PAGE,
      err_type: type,
      err_msg: clip(msg, 100) || '(none)',
      err_index: sentCount
    };
    if (vid) p.vid = vid;
    var f = shortFile(file);
    if (f) p.err_file = f;
    if (line) p.err_line = line;
    if (col) p.err_col = col;

    if (consentOK()) {
      if (!deliver(p)) pending.push(p);
    } else if (pending.length < MAX_PER_PAGE) {
      pending.push(p);
    }
    if (DEBUG) { try { console.log('[wellf-errors]', p); } catch (e) {} }
  }

  // ── drain anything the inline bootstrap in theme.liquid caught before this file ran.
  // This file is the LAST script in the document, so without that bootstrap every error
  // thrown during page setup was invisible: verified 2026-08-31 against a ReferenceError
  // in predictive-search.js that Clarity saw 7 times in 5 days and this beacon saw zero
  // times. Draining through report() means the cap, the dedup and the extension filter
  // all still apply. Nulling the queue afterwards stops the bootstrap double-reporting
  // errors that happen from now on, which our own listeners below already cover. ──
  try {
    var queued = window.__wellfErrQ;
    window.__wellfErrQ = null;
    if (queued && queued.length) {
      for (var qi = 0; qi < queued.length; qi++) {
        var q = queued[qi];
        report(q[0], q[1], q[2], q[3], q[4]);
      }
    }
  } catch (e) {}

  // ── uncaught errors, and failed asset loads (capture phase: resource errors do not
  // bubble, so a listener on window only sees them during capture) ──
  window.addEventListener('error', function (ev) {
    try {
      var t = ev && ev.target;
      if (t && t !== window && t.tagName) {
        // A resource failed to load: image, script, stylesheet.
        var src = t.src || t.href || '';
        if (isNoise(src)) return;
        report('resource', t.tagName.toLowerCase() + ' failed to load', src, 0, 0);
        return;
      }
      // Cross-origin scripts are reported as a bare "Script error." with no detail.
      // Still worth counting: it means a third-party script is throwing.
      if (!ev.filename && /script error/i.test(ev.message || '')) {
        report('crossorigin', 'Script error (cross-origin, no detail)', '', 0, 0);
        return;
      }
      report('error', ev.message, ev.filename, ev.lineno, ev.colno);
    } catch (e) {}
  }, true);

  window.addEventListener('unhandledrejection', function (ev) {
    try {
      var r = ev && ev.reason;
      var msg = (r && (r.message || r.toString && r.toString())) || 'unhandled rejection';
      var file = (r && r.stack) ? (String(r.stack).split('\n')[1] || '') : '';
      report('rejection', msg, file.replace(/^\s*at\s*/, ''), 0, 0);
    } catch (e) {}
  });

  // Consent can arrive after an early error; deliver on grant and on the way out.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush, { passive: true });
  try { document.addEventListener('visitorConsentCollected', flush); } catch (e) {}
  setTimeout(flush, 5000);

  if (DEBUG) { try { console.log('[wellf-errors] armed on', PAGE); } catch (e) {} }
})();
